import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  useTheme
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { AdminOfflineGuard } from '@components/guards/AdminOfflineGuard';
import useOnlineStatus from '@hooks/useOnlineStatus';
import {
  useStationDevicesQuery,
  useUpsertStationDevice,
  useDeleteRobot,
  useSetActiveRobot
} from '@api/bff/stationDevices';
import type { Robot } from '@api/bff/stationDevices';
import { StationDevicesHeader } from './components/StationDevicesHeader';
import RobotList from './components/RobotList';
import RobotFormDialog from './components/RobotFormDialog';
import PowerSettingsCard from './components/PowerSettingsCard';

export const StationDevices = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();

  const { robots, activeStationId, isLoading } = useStationDevicesQuery();
  const upsertMutation = useUpsertStationDevice();
  const deleteMutation = useDeleteRobot();
  const setActiveMutation = useSetActiveRobot();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Robot | null>(null);

  const isBusy =
    upsertMutation.isPending ||
    deleteMutation.isPending ||
    setActiveMutation.isPending;

  const activeRobot =
    robots.find(r => r.stationId === activeStationId) ?? robots[0] ?? null;
  const stationLabel = activeStationId || '<stationId>';
  const deviceTypeLabel = activeRobot?.deviceType || '<deviceType>';
  const deviceIdLabel = activeRobot?.mqttDeviceId || '<deviceId>';

  const handleAdd = useCallback(() => {
    setEditing(null);
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((robot: Robot) => {
    setEditing(robot);
    setDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => setDialogOpen(false), []);

  const handleSubmit = useCallback(
    async (robot: Robot) => {
      await upsertMutation.mutateAsync(robot);
      setDialogOpen(false);
    },
    [upsertMutation]
  );

  const handleDelete = useCallback(
    (id: string) => deleteMutation.mutate(id),
    [deleteMutation]
  );

  const handleSetActive = useCallback(
    (stationId: string) => setActiveMutation.mutate(stationId),
    [setActiveMutation]
  );

  // Deep link from the script builder's "Station power settings" button
  // (/settings/administration/station-devices?power=1): scroll the power card into view, and open
  // the active robot's editor when asked for directly (?power=edit). Runs once per navigation.
  const [searchParams, setSearchParams] = useSearchParams();
  const powerParam = searchParams.get('power');
  const handledDeepLink = useRef(false);

  useEffect(() => {
    if (!powerParam || isLoading || handledDeepLink.current) return;
    handledDeepLink.current = true;

    if (powerParam === 'edit' && activeRobot) {
      setEditing(activeRobot);
      setDialogOpen(true);
    } else {
      document
        .getElementById('power-settings')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Drop the param so a refresh or back-navigation doesn't re-trigger it.
    searchParams.delete('power');
    setSearchParams(searchParams, { replace: true });
  }, [powerParam, isLoading, activeRobot, searchParams, setSearchParams]);

  if (isLoading) return <LoadingSpinner />;
  if (!isOnline) return <AdminOfflineGuard children={null} />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: theme.palette.background.default
      }}
    >
      <StationDevicesHeader
        hasUnsavedChanges={false}
        isSaving={false}
        onSave={() => {}}
        onDiscard={() => {}}
      />

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          px: { xs: 2, sm: 3, md: 4 },
          py: 3
        }}
      >
        <Box sx={{ maxWidth: 600, margin: '0 auto' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 3
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('settings:stationDevices.description')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAdd}
              disabled={isBusy}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              {t('settings:stationDevices.actions.add')}
            </Button>
          </Box>

          <RobotList
            robots={robots}
            activeStationId={activeStationId}
            isBusy={isBusy}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSetActive={handleSetActive}
          />

          <PowerSettingsCard robot={activeRobot} onEdit={handleEdit} />

          <Card sx={{ mt: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                How it works
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each robot is a calibration station identified by its{' '}
                <strong>Station ID</strong>. When a script runs, the backend uses
                the active robot's <strong>Device Type</strong> and{' '}
                <strong>MQTT Device ID</strong> to build the MQTT topic it sends
                commands to:
              </Typography>

              <Box
                component="pre"
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  overflowX: 'auto',
                  mb: 2
                }}
              >
                {`voltbot/${stationLabel}/cmd/${deviceTypeLabel}/${deviceIdLabel}/<action>`}
              </Box>

              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mt: 3, mb: 1 }}
              >
                What your device needs to do
              </Typography>

              <Box
                component="ol"
                sx={{
                  pl: 2.5,
                  m: 0,
                  '& li': {
                    mb: 1.5,
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  },
                  '& li strong': { color: 'text.primary' }
                }}
              >
                <li>
                  <strong>Connect</strong> to the same HiveMQ cluster using valid
                  credentials.
                </li>
                <li>
                  <strong>Subscribe</strong> to its command topic:
                  <Box
                    component="code"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem'
                    }}
                  >
                    {`voltbot/${stationLabel}/cmd/${deviceTypeLabel}/${deviceIdLabel}/#`}
                  </Box>
                </li>
                <li>
                  <strong>Publish status</strong> so the station knows it's
                  online:
                  <Box
                    component="code"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem'
                    }}
                  >
                    {`voltbot/${stationLabel}/status/${deviceTypeLabel}/${deviceIdLabel}`}
                  </Box>
                </li>
                <li>
                  <strong>Handle commands</strong> — when the station sends a
                  command (e.g. <code>set-output</code>, <code>read</code>), your
                  device receives a JSON payload like:
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.5,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {`{ "action": "set-output", "payload": { "value": 10.0, "unit": "V" } }`}
                  </Box>
                </li>
                <li>
                  <strong>Respond</strong> using{' '}
                  <strong>MQTT 5 response topics</strong> with the correlation
                  data from the incoming message. The station waits for a
                  response like:
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.5,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {`{ "success": true }`}
                  </Box>
                </li>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                The built-in test robot in the backend serves as a reference
                implementation for how your device should handle commands and
                responses.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <RobotFormDialog
        open={dialogOpen}
        initial={editing}
        isSaving={upsertMutation.isPending}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};
